import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useLanguage } from "../App";
import { NotificationBell } from "./NotificationBell";
import { SearchBar } from "./SearchBar";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import {
  HardHat,
  LayoutDashboard,
  Building2,
  Clock,
  FileText,
  Image,
  AlertTriangle,
  ClipboardCheck,
  Settings,
  LogOut,
  Menu,
  ChevronRight,
  Globe,
  Calendar,
  DollarSign,
  BarChart3,
  Users,
  Shield
} from "lucide-react";

export const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navItems = [
    { icon: LayoutDashboard, label: t("dashboard"), href: "/dashboard" },
    { icon: Building2, label: t("projects"), href: "/projects" },
    { icon: BarChart3, label: language === "sv" ? "Tidplan" : "Timeline", href: "/gantt" },
    { icon: Clock, label: t("timeReport"), href: "/time-report" },
    { icon: Calendar, label: language === "sv" ? "Dagrapport" : "Daily Report", href: "/daily-reports" },
    { icon: FileText, label: t("changeOrders"), href: "/change-orders" },
    { icon: DollarSign, label: language === "sv" ? "Budget" : "Budget", href: "/budget" },
    { icon: Image, label: t("drawings"), href: "/drawings" },
    { icon: AlertTriangle, label: t("incidents"), href: "/incidents" },
    { icon: ClipboardCheck, label: t("inspections"), href: "/inspections" },
    { icon: Shield, label: "KMA", href: "/kma" },
    { icon: Users, label: language === "sv" ? "Team" : "Team", href: "/team" },
    { icon: Settings, label: t("settings"), href: "/settings" },
  ];

  const NavLink = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.href || 
      (item.href !== "/dashboard" && location.pathname.startsWith(item.href));
    
    return (
      <Link
        to={item.href}
        onClick={() => mobile && setMobileOpen(false)}
        className={`sidebar-item ${isActive ? 'active' : ''}`}
        data-testid={`nav-${item.href.replace('/', '')}`}
      >
        <item.icon className="w-5 h-5" />
        <span>{item.label}</span>
        {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:w-64 lg:flex-col bg-white border-r border-slate-200">
        <div className="flex items-center gap-2 h-16 px-6 border-b border-slate-200">
          <img src="/controlify-logo.png" alt="Controlify" className="w-8 h-8 object-contain" />
          <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
            Controlify
          </span>
        </div>
        
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink key={item.href} item={item} />
          ))}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3">
            <Avatar>
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-primary text-white">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between h-14 px-4">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <div className="flex items-center gap-2 h-14 px-4 border-b border-slate-200">
                <img src="/controlify-logo.png" alt="Controlify" className="w-7 h-7 object-contain" />
                <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                  Controlify
                </span>
              </div>
              <nav className="px-3 py-4 space-y-1">
                {navItems.map((item) => (
                  <NavLink key={item.href} item={item} mobile />
                ))}
              </nav>
              <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
                <Button 
                  variant="ghost" 
                  className="w-full justify-start text-red-600"
                  onClick={handleLogout}
                  data-testid="mobile-logout-btn"
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  {t("logout")}
                </Button>
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-2">
            <img src="/controlify-logo.png" alt="Controlify" className="w-6 h-6 object-contain" />
            <span className="font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              Controlify
            </span>
          </div>

          <div className="flex items-center gap-1">
            <NotificationBell />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-btn">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-primary text-white text-sm">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setLanguage(language === "sv" ? "en" : "sv")}>
                <Globe className="w-4 h-4 mr-2" />
                {language === "sv" ? "English" : "Svenska"}
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings">
                  <Settings className="w-4 h-4 mr-2" />
                  {t("settings")}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                {t("logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>
      <header className="hidden lg:block fixed top-0 left-64 right-0 z-40 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between h-16 px-6">
          <SearchBar />
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === "sv" ? "en" : "sv")}
              data-testid="desktop-lang-toggle"
            >
              <Globe className="w-4 h-4 mr-2" />
              {language === "sv" ? "EN" : "SV"}
            </Button>

            <NotificationBell />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2" data-testid="desktop-user-menu">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-primary text-white text-sm">
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings">
                    <Settings className="w-4 h-4 mr-2" />
                    {t("settings")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64 lg:pt-16 min-h-screen">
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 safe-bottom z-40">
        <div className="flex items-center justify-around h-16">
          {[
            { icon: LayoutDashboard, label: t("dashboard"), href: "/dashboard" },
            { icon: Building2, label: t("projects"), href: "/projects" },
            { icon: Clock, label: t("timeReport"), href: "/time-report" },
            { icon: FileText, label: "ÄTA", href: "/change-orders" },
          ].map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href !== "/dashboard" && location.pathname.startsWith(item.href));
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`mobile-nav-item ${isActive ? 'active' : ''}`}
                data-testid={`mobile-nav-${item.href.replace('/', '')}`}
              >
                <item.icon className="w-5 h-5 mb-1" />
                <span className="text-[10px]">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};
